/**
 * Coups de pouce — Attendu 1126 « Calculer un effectif manquant dans un
 * tableau pour un petit nombre de valeurs » (automatisme 4e).
 *
 * manquant_* (saisie, QCM) et vf_tableau_vf : entrée PARTAGÉE — le total est la
 * somme de tous les effectifs, on additionne les cases visibles puis on
 * soustrait au total, on vérifie en refaisant la somme. L'exemple (revue
 * 2026-09-07) : instruments de musique, 9 / ? / 5 / 4, total 30 → 30 − 18 = 12,
 * contrôle par la somme. SVG piloté par les flèches de la sheet (data-etapes /
 * data-caption).
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

// Tableau dessiné en SVG : en-tête 5 colonnes + Total, ligne Effectif
const COLS = ['Instrument', 'Piano', 'Guitare', 'Batterie', 'Violon', 'Total'];
const W = [74, 46, 52, 54, 48, 46]; const X0 = 20; const XS = W.reduce((acc, w, i) => { acc.push(i === 0 ? X0 : acc[i - 1] + W[i - 1]); return acc; }, []);
const cell = (i, y, txt, fill, couleur, gras) => `<rect x="${XS[i]}" y="${y}" width="${W[i]}" height="22" fill="${fill}" stroke="#94A3B8" stroke-width="1"/>`
    + `<text x="${XS[i] + W[i] / 2}" y="${y + 15}" font-size="11" font-weight="${gras ? 700 : 600}" fill="${couleur}" text-anchor="middle">${txt}</text>`;
const TABLEAU = (manque) => COLS.map((c, i) => cell(i, 40, c, '#1E3A8A', '#ffffff', true)).join('')
    + cell(0, 62, 'Effectif', '#1E3A8A', '#ffffff', true) + cell(1, 62, '9', '#ffffff', '#1e293b') + cell(2, 62, manque, '#FFF7ED', '#B45309', true)
    + cell(3, 62, '5', '#ffffff', '#1e293b') + cell(4, 62, '4', '#ffffff', '#1e293b') + cell(5, 62, '30', '#ffffff', '#1e293b', true);

const SVG_MANQUANT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Retrouver un effectif effacé dans un tableau, en 3 étapes à faire défiler avec les flèches. Tableau des instruments : piano 9, guitare effacée, batterie 5, violon 4, total 30. Étape 1 : on additionne les cases visibles, 9 plus 5 plus 4 égale 18. Étape 2 : on retire cette somme au total, 30 moins 18 égale 12. Étape 3 : on vérifie, 9 plus 12 plus 5 plus 4 égale 30, toutes les cases remises ensemble redonnent le total">
  <style>
    @keyframes cp1126-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1126-pop { opacity: 0; animation: cp1126-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Une case a été effacée : combien de guitares ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">On retire au total la somme des autres</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On vérifie, et on évite le piège</text>

  <!-- ── Le tableau et la somme des cases visibles (étape 1) ─────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${TABLEAU('?')}
    <text x="8" y="106" font-size="11.5" font-weight="700" fill="#1E3A8A">Les cases visibles : 9 + 5 + 4 = 18</text>
    <text x="8" y="121" font-size="10" font-weight="600" fill="#475569">le total 30 compte aussi la case effacée</text>
  </g>

  <!-- ── La soustraction (étape 2) ───────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1126-pop">
      <text x="8" y="150" font-size="11.5" font-weight="700" fill="#1D9E75">Effectif manquant = total − (somme des autres)</text>
      <text x="8" y="166" font-size="12.5" font-weight="800" fill="#1D9E75">30 − 18 = 12 guitares ✓</text>
    </g>
  </g>

  <!-- ── Contrôle et piège (étape 3) ─────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1126-pop">
      <text x="8" y="194" font-size="11.5" font-weight="700" fill="#B45309">Contrôle : 9 + 12 + 5 + 4 = 30 ✓</text>
      <text x="8" y="212" font-size="10" font-weight="600" fill="#B45309">toutes les cases remises ensemble redonnent le total</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Total − (somme des autres), puis on contrôle.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_MANQUANT = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Le total est la somme de tous les effectifs**, la case effacée comprise.',
                "**Additionne les effectifs que tu vois**, puis retire cette somme au total : effectif manquant = total − (somme des autres). C'est une addition à trou, qu'on complète par une soustraction.",
                '**Vérifie** : en ajoutant ta réponse aux autres effectifs, tu dois retrouver le total.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_MANQUANT },
    ],
};

// ─── Quel calcul ? : deux tableaux, catégorie effacée puis Total effacé ────
const TABLEAU_Y = (y, guitare, total) => COLS.map((c, i) => cell(i, y, c, '#1E3A8A', '#ffffff', true)).join('')
    + cell(0, y + 22, 'Effectif', '#1E3A8A', '#ffffff', true) + cell(1, y + 22, '9', '#ffffff', '#1e293b')
    + cell(2, y + 22, guitare, guitare === '?' ? '#FFF7ED' : '#ffffff', guitare === '?' ? '#B45309' : '#1e293b', guitare === '?')
    + cell(3, y + 22, '5', '#ffffff', '#1e293b') + cell(4, y + 22, '4', '#ffffff', '#1e293b')
    + cell(5, y + 22, total, total === '?' ? '#FFF7ED' : '#ffffff', total === '?' ? '#B45309' : '#1e293b', true);

const SVG_CALCUL = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Choisir le calcul selon la case effacée, en 3 étapes à faire défiler avec les flèches. Étape 1 : la case Guitare est effacée, on soustrait au total la somme de toutes les autres cases, 30 moins, entre parenthèses, 9 plus 5 plus 4. Étape 2 : la case Total est effacée, on additionne toutes les cases sans en oublier une, 9 plus 12 plus 5 plus 4. Étape 3 : catégorie effacée, on soustrait ; total effacé, on additionne">
  <style>
    @keyframes cp1126-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1126-pop { opacity: 0; animation: cp1126-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Une catégorie effacée : on soustrait</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Le total effacé : on additionne</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">D'abord repérer la case qui manque</text>

  <!-- ── Catégorie effacée (étape 1) ─────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${TABLEAU_Y(32, '?', '30')}
    <text x="8" y="96" font-size="11.5" font-weight="700" fill="#1E3A8A">Guitare = 30 − (9 + 5 + 4) ✓</text>
    <text x="8" y="111" font-size="10" font-weight="600" fill="#1E3A8A">on retire au total la somme de toutes les autres cases</text>
  </g>

  <!-- ── Total effacé (étape 2) ──────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1126-pop">
      ${TABLEAU_Y(126, '12', '?')}
      <text x="8" y="190" font-size="11.5" font-weight="700" fill="#1D9E75">Total = 9 + 12 + 5 + 4 ✓</text>
      <text x="8" y="205" font-size="10" font-weight="600" fill="#1D9E75">on additionne toutes les cases, sans en oublier une</text>
    </g>
  </g>

  <!-- ── Synthèse (étape 3) ──────────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1126-pop">
      <text x="180" y="232" font-size="11.5" font-weight="700" fill="#B45309" text-anchor="middle">Catégorie effacée : on soustrait. Total effacé : on additionne.</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Regarde la case qui manque avant de calculer.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_CALCUL = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Regarde d'abord quelle case manque.** Si c'est une catégorie, on soustrait ; si c'est le total, on additionne.",
                '**Une catégorie effacée** : total − (somme de toutes les autres). Les parenthèses regroupent tout ce qu\'on retire, pas une seule catégorie.',
                '**Le total effacé** : on additionne tous les effectifs, sans en oublier un.',
                '**Un calcul juste donne un résultat possible** : plus petit que le total pour une catégorie, plus grand que chaque effectif pour le total.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_CALCUL },
    ],
};

// ─── Tableau à double entrée : droitiers / gauchers × lunettes / sans ─────
// Colonnes : [libellé, Lunettes, Sans lunettes, Total] ; lignes y : en-tête 34, Droitiers 56, Gauchers 78, Total 100
const DX = [40, 120, 196, 282]; const DW = [80, 76, 86, 48];
const dcell = (i, y, txt, fill, couleur, gras) => `<rect x="${DX[i]}" y="${y}" width="${DW[i]}" height="22" fill="${fill}" stroke="#94A3B8" stroke-width="1"/>`
    + `<text x="${DX[i] + DW[i] / 2}" y="${y + 15}" font-size="11" font-weight="${gras ? 700 : 600}" fill="${couleur}" text-anchor="middle">${txt}</text>`;
const TETE = '#1E3A8A'; const BLANC = '#ffffff'; const NOIR = '#1e293b';
const TABLEAU_D = dcell(0, 34, '', TETE, BLANC) + dcell(1, 34, 'Lunettes', TETE, BLANC, true) + dcell(2, 34, 'Sans lunettes', TETE, BLANC, true) + dcell(3, 34, 'Total', TETE, BLANC, true)
    + dcell(0, 56, 'Droitiers', TETE, BLANC, true) + dcell(1, 56, '14', BLANC, NOIR) + dcell(2, 56, '?', '#FFF7ED', '#B45309', true) + dcell(3, 56, '19', BLANC, NOIR, true)
    + dcell(0, 78, 'Gauchers', TETE, BLANC, true) + dcell(1, 78, '3', BLANC, NOIR) + dcell(2, 78, '2', BLANC, NOIR) + dcell(3, 78, '5', BLANC, NOIR, true)
    + dcell(0, 100, 'Total', TETE, BLANC, true) + dcell(1, 100, '17', BLANC, NOIR, true) + dcell(2, 100, '7', BLANC, NOIR, true) + dcell(3, 100, '24', BLANC, NOIR, true);

const SVG_DOUBLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Retrouver une case effacée dans un tableau à double entrée, en 3 étapes à faire défiler avec les flèches. Tableau droitiers et gauchers, avec ou sans lunettes : droitiers 14 et une case effacée, total 19 ; gauchers 3 et 2, total 5 ; totaux des colonnes 17 et 7, total général 24. Étape 1 : la case effacée est dans la ligne Droitiers et la colonne Sans lunettes. Étape 2 : par la ligne, 19 moins 14 égale 5. Étape 3 : par la colonne, 7 moins 2 égale 5, même résultat">
  <style>
    @keyframes cp1126-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1126-pop { opacity: 0; animation: cp1126-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">La case effacée : sa ligne et sa colonne</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Par la ligne : total de la ligne − l'autre case</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On vérifie par la colonne</text>

  <!-- ── Le tableau (étape 1) ────────────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${TABLEAU_D}
    <text x="8" y="140" font-size="11.5" font-weight="700" fill="#1E3A8A">Combien de droitiers sans lunettes ?</text>
    <text x="8" y="155" font-size="10" font-weight="600" fill="#475569">la case est dans la ligne Droitiers (total 19) et la colonne Sans lunettes (total 7)</text>
  </g>

  <!-- ── Par la ligne (étape 2) ──────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1126-pop">
      <rect x="40" y="56" width="290" height="22" fill="none" stroke="#1D9E75" stroke-width="2.5" rx="3"/>
      <text x="8" y="182" font-size="11.5" font-weight="700" fill="#1D9E75">Par la ligne : 19 − 14 = 5 ✓</text>
    </g>
  </g>

  <!-- ── Par la colonne (étape 3) ────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1126-pop">
      <rect x="196" y="34" width="86" height="88" fill="none" stroke="#B45309" stroke-width="2.5" rx="3"/>
      <text x="8" y="206" font-size="11.5" font-weight="700" fill="#B45309">Par la colonne : 7 − 2 = 5 ✓  le même nombre</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Un sens pour calculer, l'autre pour vérifier.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_DOUBLE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Chaque case intérieure appartient à une ligne et à une colonne.** La case Total de sa ligne est la somme des cases de cette ligne ; la case Total de sa colonne est la somme des cases de cette colonne.',
                "**Pour retrouver la case effacée, choisis sa ligne ou sa colonne** : case effacée = total de la ligne − l'autre case de la ligne. Tu peux aussi le faire avec la colonne.",
                "**Vérifie avec l'autre sens** : le calcul par la colonne doit donner le même nombre.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_DOUBLE },
    ],
};

const CP_1126 = {
    manquant_saisie: AIDE_MANQUANT,
    manquant_qcm_unique: AIDE_MANQUANT,
    calcul_qcm_unique: AIDE_CALCUL,
    double_saisie: AIDE_DOUBLE,
    vf_tableau_vf: AIDE_MANQUANT,
};

export default CP_1126;
